import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'

import { State } from './state'

/**
 * Core actions for the survey configuration feature
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig() {
  return {
    /** Clears any loaded state, typically when scenario context changes */
    clear: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: () => State.INITIAL,
    }),
    /**
     * Updates the local functional-usage configuration values.
     */
    updateFunctionalUsage: store.actions.standalone({
      data: store.actions.Data.asIs<
        Partial<ents.extra.space.SurveyConfiguration.FunctionalUsage>
      >(),
      reducer: ({ state, data }) => {
        const editedConfig = State.editedConfigOrDefault(state)

        return {
          ...state,
          editedConfig: {
            ...editedConfig,
            functionalUsage: {
              ...(editedConfig.functionalUsage ??
                ents.extra.space.SurveyConfiguration.FunctionalUsage.DEFAULT),
              ...data,
            },
          },
        }
      },
    }),
    /**
     * Updates the local survey-tools configuration values.
     */
    updateTools: store.actions.standalone({
      data: store.actions.Data.asIs<
        Partial<ents.extra.space.SurveyConfiguration.Tools>
      >(),
      reducer: ({ state, data }) => {
        const editedConfig = State.editedConfigOrDefault(state)

        return {
          ...state,
          editedConfig: {
            ...editedConfig,
            tools: { ...editedConfig.tools, ...data },
          },
        }
      },
    }),
    /**
     * Loads survey configuration for the current scenario context
     */
    load: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<
        ents.extra.space.SurveyConfiguration | undefined
      >(),
      reducers: {
        request: () => ({
          ...State.INITIAL,
          isLoading: true,
        }),
        success: ({ state, data: { response } }) => ({
          ...state,
          config: response,
          editedConfig:
            response ?? ents.extra.space.SurveyConfiguration.Input.DEFAULT,
          error: undefined,
          isLoading: false,
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          error,
          isLoading: false,
        }),
        cancellation: ({ state }) => ({
          ...state,
          isLoading: false,
        }),
      },
    }),
    /**
     * Upserts survey configuration for the current scenario context
     */
    upsert: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<void>(),
      reducers: {
        request: ({ state }) => ({
          ...state,
          error: undefined,
          isLoading: true,
        }),
        success: ({ state }) => ({
          ...state,
          isLoading: false,
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          error,
          isLoading: false,
        }),
        cancellation: ({ state }) => ({
          ...state,
          isLoading: false,
        }),
      },
    }),
  } as const satisfies store.actions.Config.Constraint<State>
}
